/**
 * Decision Board Component
 * Displays strategic analytics and metrics for client issues
 * Mobile-optimized with responsive table/card switching
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import DecisionCard from '../components/decision-board/DecisionCard';
import IssueTerrain3D from '../components/IssueTerrain3D';
import './DecisionBoard.css';

// Type definitions
interface CheckmateRisk {
  score: number;
  level: string;
  color: string;
  act_issues_count: number;
  high_influence_opposed_count: number;
}

interface Posture {
  score: number;
  category: string;
  color: string;
  article_component: number;
  stakeholder_component: number;
}

interface Spillover {
  percentage: number;
  index: number;
  risk_level: string;
  color: string;
  shared_stakeholders: number;
  total_stakeholders: number;
}

interface Velocity {
  fv_score: number;
  recent_articles_24h: number;
  trend: string;
}

interface Issue {
  id: string;
  title: string;
  description: string;
  overall_score: number;
  decision_mode: string;
  posture: Posture;
  velocity: Velocity;
  spillover: Spillover;
  stakeholder_count: number;
  article_count: number;
  priority_level: string;
  status: string;
}

interface Summary {
  total_issues: number;
  act_count: number;
  prepare_count: number;
  monitor_count: number;
  act_percentage: number;
  prepare_percentage: number;
  monitor_percentage: number;
}

interface DecisionBoardData {
  client_id: string;
  timestamp: string;
  checkmate_risk: CheckmateRisk;
  summary: Summary;
  issues: Issue[];
}

const DecisionBoard: React.FC = () => {
  const [data, setData] = useState<DecisionBoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<Issue | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const terrainHeroRef = useRef<HTMLDivElement>(null);

  const toggleFullscreen = useCallback(() => {
    const el = terrainHeroRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // Auto-fullscreen on landscape rotation (Android/desktop); graceful no-op on iOS Safari
  useEffect(() => {
    const getIsLandscape = () =>
      window.screen?.orientation
        ? !window.screen.orientation.type.includes('portrait')
        : window.innerWidth > window.innerHeight;

    const handleOrientationChange = () => {
      const el = terrainHeroRef.current;
      if (!el) return;
      if (getIsLandscape()) {
        // Enter fullscreen when rotating to landscape
        if (!document.fullscreenElement && document.fullscreenEnabled) {
          el.requestFullscreen().catch(() => {});
        }
      } else {
        // Exit fullscreen when rotating back to portrait
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
      }
    };

    // Modern API
    if (window.screen?.orientation) {
      window.screen.orientation.addEventListener('change', handleOrientationChange);
    }
    // Legacy fallback
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      if (window.screen?.orientation) {
        window.screen.orientation.removeEventListener('change', handleOrientationChange);
      }
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    loadDecisionBoard();
  }, []);

  const loadDecisionBoard = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getDecisionBoard();
      setData(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load Decision Board');
      console.error('Error loading Decision Board:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await api.exportDecisionBoard();
    } catch (err: any) {
      alert('Failed to export Decision Board: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  const getDecisionModeColor = (mode: string): string => {
    switch (mode) {
      case 'ACT':
        return '#DC2626'; // Red
      case 'PREPARE':
        return '#EA580C'; // Orange
      case 'MONITOR':
        return '#16A34A'; // Green
      default:
        return '#6B7280'; // Gray
    }
  };

  if (loading) {
    return (
      <div className="decision-board-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading Decision Board...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="decision-board-container">
        <div className="error-state">
          <h3>Error Loading Decision Board</h3>
          <p>{error}</p>
          <button onClick={loadDecisionBoard} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // ── Shared issue table rows (used in metrics section below the terrain) ──
  const issueTableRows = data.issues.map((issue) => (
    <tr key={issue.id}>
      <td className="issue-title"><div className="issue-title-text">{issue.title}</div></td>
      <td><span className="heat-score">{issue.overall_score?.toFixed(1) || '0.0'}</span></td>
      <td>
        <span className="decision-mode-badge" style={{ backgroundColor: getDecisionModeColor(issue.decision_mode) }}>
          {issue.decision_mode}
        </span>
      </td>
      <td>
        <div className="posture-cell">
          <span className="posture-score" style={{ color: issue.posture?.color || '#6B7280' }}>
            {issue.posture?.score?.toFixed(2) || '0.00'}
          </span>
          <span className="posture-category">{issue.posture?.category || 'N/A'}</span>
        </div>
      </td>
      <td>
        <div className="velocity-cell">
          <span className="velocity-score">{issue.velocity?.fv_score?.toFixed(1) || '0.0'}</span>
          <span className="velocity-trend">{issue.velocity?.trend || 'stable'}</span>
        </div>
      </td>
      <td>
        <span className="spillover-percentage" style={{ color: issue.spillover?.color || '#6B7280' }}>
          {issue.spillover?.percentage?.toFixed(1) || issue.spillover?.index?.toFixed(1) || '0.0'}%
        </span>
      </td>
      <td className="centered">{issue.stakeholder_count}</td>
      <td className="centered">{issue.article_count}</td>
    </tr>
  ));

  // ── 3D TERRAIN: hero canvas fills 100vh, metrics + table scroll below ──
  return (
    <div
      style={{
        // Pull out of the layout-content padding (32px all sides)
        margin: '-32px',
        width: 'calc(100% + 64px)',
      }}
    >
      {/* ── HERO SECTION: full viewport height ── */}
      <div
        ref={terrainHeroRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          background: '#0a0f1a',
        }}
      >
        {/* Canvas fills the hero */}
        <IssueTerrain3D
          clientId={data.client_id}
          onPeakSelected={(peak) => {
            const found = data.issues.find(i => i.id === peak.issue_id);
            if (found) setSelectedIssue(found);
          }}
        />

        {/* Floating top bar — overlaid on the canvas */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 20px',
            background: 'linear-gradient(to bottom, rgba(10,15,26,0.85) 0%, transparent 100%)',
            zIndex: 20,
            pointerEvents: 'none',
          }}
        >
          <h1 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>
            Decision Board
          </h1>
          {/* Buttons need pointer events re-enabled */}
          <div style={{ display: 'flex', gap: 8, pointerEvents: 'auto' }}>
            <button
              onClick={toggleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
              style={{
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.25)',
                color: '#fff',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {isFullscreen ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"/>
                  </svg>
                  Exit Fullscreen
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                  </svg>
                  Fullscreen
                </>
              )}
            </button>
            <button
              onClick={handleExport}
              className="export-button"
              disabled={exporting}
              style={{ pointerEvents: 'auto' }}
            >
              {exporting ? 'Exporting...' : 'Export PDF'}
            </button>
          </div>
        </div>

        {/* Scroll-down hint at the bottom */}
        <div
          style={{
            position: 'absolute',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255,255,255,0.5)',
            fontSize: 12,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          <span>Scroll for metrics</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12l7 7 7-7"/>
          </svg>
        </div>
      </div>

      {/* ── SCROLLABLE METRICS BELOW THE FOLD ── */}
      <div style={{ padding: '32px', background: '#f9fafb' }}>
        {/* Checkmate Risk */}
        <div className="metric-card checkmate-risk-card">
          <div className="metric-header">
            <h2>Checkmate Risk</h2>
            <span className="help-icon" title="Threat level from high-influence opposed stakeholders">?</span>
          </div>
          <div className="metric-content">
            <div className="risk-score" style={{ color: data.checkmate_risk.color }}>
              {data.checkmate_risk.score.toFixed(1)}
            </div>
            <div className="risk-level" style={{ backgroundColor: data.checkmate_risk.color }}>
              {data.checkmate_risk.level}
            </div>
            <div className="risk-details">
              <div className="risk-detail-item">
                <span className="label">ACT Issues:</span>
                <span className="value">{data.checkmate_risk.act_issues_count}</span>
              </div>
              <div className="risk-detail-item">
                <span className="label">High-Influence Opposed:</span>
                <span className="value">{data.checkmate_risk.high_influence_opposed_count}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Summary grid */}
        <div className="summary-grid">
          <div className="summary-card">
            <h3>Total Issues</h3>
            <div className="summary-value">{data.summary.total_issues}</div>
          </div>
          <div className="summary-card act">
            <h3>ACT</h3>
            <div className="summary-value">{data.summary.act_count}</div>
            <div className="summary-percentage">{data.summary.act_percentage.toFixed(0)}%</div>
          </div>
          <div className="summary-card prepare">
            <h3>PREPARE</h3>
            <div className="summary-value">{data.summary.prepare_count}</div>
            <div className="summary-percentage">{data.summary.prepare_percentage.toFixed(0)}%</div>
          </div>
          <div className="summary-card monitor">
            <h3>MONITOR</h3>
            <div className="summary-value">{data.summary.monitor_count}</div>
            <div className="summary-percentage">{data.summary.monitor_percentage.toFixed(0)}%</div>
          </div>
        </div>

        {/* Issues table */}
        <div className="issues-section">
          <h2>Issues Overview (Reference)</h2>
          <div className="issues-table-container desktop-only">
            <table className="issues-table">
              <thead>
                <tr>
                  <th>Issue</th><th>HEAT</th><th>Mode</th><th>Posture</th>
                  <th>Velocity</th><th>Spillover</th><th>Stakeholders</th><th>Articles</th>
                </tr>
              </thead>
              <tbody>{issueTableRows}</tbody>
            </table>
          </div>
          <div className="issues-cards-container mobile-only">
            {data.issues.map((issue) => (
              <DecisionCard key={issue.id} issue={issue} getDecisionModeColor={getDecisionModeColor} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecisionBoard;
